import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@organizacaox/lai-design-system/query";
import {
  getSupportConfig,
  getSupportTicket,
  listSupportTickets,
  createSupportTicket,
  uploadTicketFiles,
  commentSupportTicket,
  closeSupportTicket,
  deleteSupportTicket,
  reopenSupportTicket,
} from "@/api/support";
import { type MutationHandlers } from "./mutation-handlers";

// Hooks do domínio de SUPORTE (chamado aberto no Disk, atendido no Jira).
// Mesmo padrão de use-costs.ts:
//   - `src/api/support.ts` guarda o request (fetch + tipos), sem React;
//   - este arquivo guarda as CHAVES e os hooks do TanStack Query;
//   - a tela não importa a função de api: importa o hook.
//

// As chaves DESTE domínio, como já estavam nas telas — string por string.
//
// Duas ressalvas herdadas:
//  - `config` NÃO leva a marca ativa, mas `tickets`/`ticket` levam. A feature é
//    ligada por org no back, então em rigor a config também seria por marca;
//    na prática a mesma conta tem a mesma configuração de Jira, e partir a
//    entrada agora faria a sidebar rebuscar a cada troca de marca. Divergência
//    pré-existente, preservada;
//  - `ticket` leva orgId ANTES do id (["support-ticket", orgId, id]), o que
//    torna ["support-ticket", orgId] um prefixo útil — mas ninguém o usa hoje:
//    quem escreve invalida a chave completa e a lista.
export const supportKeys = {
  config: ["support-config"] as const,
  tickets: (orgId: string | undefined) => ["support-tickets", orgId] as const,
  ticket: (orgId: string | undefined, id: string) => ["support-ticket", orgId, id] as const,
};

// Gate fail-closed da feature. `retry` é parâmetro porque as pontas divergem e
// a divergência é preservada: a navegação (src/lib/support-enabled.ts) usa
// `retry: false` — item de menu que some é melhor que menu que treme —, e as
// quatro telas usam o default do cliente. Mesma chave, mesma entrada de cache.
export function useSupportConfig(options: { retry?: boolean } = {}) {
  return useQuery({
    queryKey: supportKeys.config,
    queryFn: getSupportConfig,
    ...(options.retry === undefined ? {} : { retry: options.retry }),
  });
}

// O back sincroniza a lista com cache curto e concorrência limitada no Jira.
// Atualiza ao retornar à tela e a cada minuto enquanto a aba estiver visível.
// `enabled` espera a config CONFIRMADA como ligada.
export function useSupportTickets(orgId: string | undefined, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: supportKeys.tickets(orgId),
    queryFn: listSupportTickets,
    enabled: options.enabled,
    staleTime: 0,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}

export function useSupportTicket(
  orgId: string | undefined,
  id: string,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: supportKeys.ticket(orgId, id),
    queryFn: () => getSupportTicket(id),
    enabled: options.enabled,
    // A tela do chamado não tem botão de atualizar: com o staleTime global de
    // 5 min, a resposta do suporte demoraria a aparecer. 30 s deixa o refoco da
    // aba e a volta para a tela buscarem de novo — sem polling, porque o rate
    // limit da Atlassian é da conta inteira.
    staleTime: 30_000,
  });
}

// Responder e alternar fechamento mexem no MESMO par (o chamado e a lista), e
// nesta ordem. `useCallback` porque a tela a chama de dentro de handlers async
// e ela pode acabar em array de dependência.
export function useInvalidateSupportTicket(orgId: string | undefined, id: string) {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: supportKeys.ticket(orgId, id) });
    // A lista mostra o mesmo estado: sem invalidar as duas, voltar para
    // "Meus chamados" mostraria o chamado aberto por até 5 min (staleTime).
    await queryClient.invalidateQueries({ queryKey: supportKeys.tickets(orgId) });
  }, [queryClient, orgId, id]);
}

export function useCreateTicketWithAttachments(
  input: Parameters<typeof createSupportTicket>[0],
  files: File[],
  handlers: MutationHandlers<{
    criado: Awaited<ReturnType<typeof createSupportTicket>>;
    anexoFalhou: boolean;
  }> = {},
) {
  return useMutation({
    mutationFn: async () => {
      const criado = await createSupportTicket(input);
      if (files.length) {
        try {
          await uploadTicketFiles(criado.id, { files });
        } catch {
          return { criado, anexoFalhou: true };
        }
      }
      return { criado, anexoFalhou: false };
    },
    ...handlers,
  });
}

export function useRetryTicketAttachments(
  ticketId: string | undefined,
  files: File[],
  handlers: MutationHandlers<void> = {},
) {
  return useMutation({
    mutationFn: async () => {
      if (ticketId) await uploadTicketFiles(ticketId, { files });
    },
    ...handlers,
  });
}

export function useReplyToSupportTicket(orgId: string | undefined, id: string) {
  const invalidate = useInvalidateSupportTicket(orgId, id);
  return useMutation({
    mutationFn: async ({ files, text }: { files: File[]; text: string }) => {
      if (files.length) await uploadTicketFiles(id, { files, text });
      else await commentSupportTicket(id, text);
    },
    onSuccess: invalidate,
  });
}
// Excluído some de vez: tira o chamado do cache (refazer a busca daria 404)
// e atualiza a lista, para onde a tela volta em seguida.
export function useDeleteSupportTicket(orgId: string | undefined, id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => deleteSupportTicket(id),
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: supportKeys.ticket(orgId, id) });
      await queryClient.invalidateQueries({ queryKey: supportKeys.tickets(orgId) });
    },
  });
}

export function useToggleSupportTicketClosed(orgId: string | undefined, id: string) {
  const invalidate = useInvalidateSupportTicket(orgId, id);
  return useMutation({
    mutationFn: (closed: boolean) => (closed ? reopenSupportTicket(id) : closeSupportTicket(id)),
    onSuccess: invalidate,
  });
}
